import { useCallback, useMemo, useRef, useState } from 'react';
import { CallBadge } from './CallBadge';
import { colourwaysOf, useSubmodels } from './listingStore';
import { reasoningTooltip } from './ReasoningBadge';
import {
  listingCropsPrompt, listingPictures, rawListingBoxes, runListingCrops,
  useListingCropAsks,
  useListingCrops,
} from './listingCropsRun';
import { StepPromptNav } from './StepPromptNav';
import { BoxOverlay } from './BoxOverlay';
import { partTone } from './ListingCropCell';
import { useSubmodelFilter } from './submodelFilterStore';
import { renderPartCrops } from './partCrop';
import { haltGate } from './runHalt';
import type { PartItem } from './partsRun';
import { useLanguage } from '../../contexts/LanguageContext';

// WHERE EACH PART IS IN THE LISTING PHOTOGRAPHS: the question, and what every photograph
// answered. Beside the phase's run button, because that button now asks it.
//
// It was on the LISTING CROPS row, under the button that was the only way to start the
// search. The search runs from the phase press now, so the badge belongs where the press is
// — see PartsPhase, where the four of them sit in the order they run. The row keeps its
// button, relabelled: what is left there is a re-run.
//
// ONE CALL PER PHOTOGRAPH, WALKED WITH ↑ ↓. The words are the same every time and only image 1
// changes, so the panel shows one photograph's call at a time — that photograph, the drawings
// that go with it, and what it answered — and the arrows move to the next. The per-photograph
// copy, on the picture it was asked about, is still in the zoom.
export function ListingCropsInfo({ docId, file, items, only, categories, disabled, onStop }: {
  docId: string;
  file: Blob;
  // The open part, or undefined for every part — the phase button's scope, and what the table's
  // Redo on this row used to be scoped to.
  only?: number;
  // Every part's category, the database's first — see usePartCategories.
  categories: string[];
  // The parts this describes, handed in rather than read live: a re-run empties the store's
  // list before it refills it, and a prompt built from nothing is a preview of no call. See
  // PartsPhase, which keeps the last list this document had.
  items: PartItem[];
  // Something else is in flight — the whole press — or the phase is locked. See CallRun.
  disabled: boolean;
  // STOP, while this badge's run is out and was started on its own (see CallRun.onStop).
  onStop?: () => void;
}) {
  const { t } = useLanguage();
  const { notes, busy, read } = useListingCrops(docId);
  const submodel = useSubmodelFilter(docId);
  // The photographs a run reads, in the order it sends them — the same list, from the same
  // function (see listingPictures).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const photos = useMemo(() => listingPictures(docId), [docId, submodel]);
  const [photoAt, setPhotoAt] = useState(0);
  const at = Math.min(photoAt, Math.max(0, photos.length - 1));
  const photo = photos[at];

  // The drawings are rendered HERE and only when somebody opens this: they are a pass over
  // the PDF, and the panel is the one place that needs them without a call being made. The
  // prompt names them per part, so a preview built without them would number fewer pictures
  // than the press sends. Rendered ONCE per list, not once per arrow press: they are the same
  // for every photograph.
  const drawings = useRef<{ items: PartItem[]; done: Promise<(string | null)[]> } | null>(null);
  const load = useCallback(async () => {
    if (drawings.current?.items !== items) {
      drawings.current = { items, done: renderPartCrops(file, items) };
    }
    return listingCropsPrompt(docId, items, categories, await drawings.current.done, only, photo);
  }, [docId, file, items, categories, only, photo]);

  // WHAT THIS PHOTOGRAPH ANSWERED, under its own question — absent until it has. During a run,
  // only once THIS run has read it: before that the note is the last run's.
  const note = photo && (!busy || read.includes(photo)) ? notes[photo] : undefined;
  // AND WHAT THAT ANSWER LOOKS LIKE, under it: the photograph with every box the reply sent,
  // read back out of the reply itself (rawListingBoxes) — the model's own rectangles, before
  // our padding — each in its part's colour, the colour the table's crops are drawn in.
  const eligible = useListingCropAsks(items, categories);
  const answer = useMemo(() => {
    if (!note || !photo) return undefined;
    const boxes = Object.entries(rawListingBoxes(note.raw, items)).flatMap(([key, rects]) => {
      const part = Number(key);
      return rects.map(({ box }) => ({
        box,
        label: items[part]?.nameEn || items[part]?.name || t('notice.parts.piece'),
        tone: partTone(eligible.indexOf(part)),
      }));
    });
    return {
      label: t('notice.parts.listingAnswer'),
      content: (
        <div className="space-y-4">
          {reasoningTooltip(note.reasoning, note.raw, t, null, note.unreadable)}
          {boxes.length > 0 && (
            <div className="flex justify-center">
              <span className="relative inline-block">
                <img
                  src={photo}
                  alt=""
                  className="block max-h-[36rem] max-w-full rounded-lg bg-white"
                />
                {boxes.map((b, n) => (
                  <BoxOverlay key={n} box={b.box} label={b.label} tone={b.tone} veil={false} />
                ))}
              </span>
            </div>
          )}
        </div>
      ),
    };
  }, [note, photo, items, eligible, t]);

  const submodels = colourwaysOf(useSubmodels(docId));
  const cropAll = async (subs: string[]) => {
    // STOPPED BETWEEN COLOURWAYS too (see runHalt).
    const halted = haltGate(docId);
    const drawn = await renderPartCrops(file, items);
    for (const sub of subs) {
      if (halted()) return;
      await runListingCrops(docId, items, categories, drawn, only, sub);
    }
  };

  return (
    <CallBadge
      title={t('notice.parts.badgeCrops')}
      named
      // THE SEARCH ON ITS OWN, for the tab you are on — the way the Size badge runs its own call.
      run={{
        // EVERY COLOURWAY by default, as the press does: Phase 3 reads them all at once.
        onRun: () => void cropAll(submodels.length > 1 ? submodels.map((s) => s.id) : [submodel]),
        // THE COLOURWAY ON SCREEN, OR EVERY ONE: the photographs are a colourway's, so the boxes
        // are too (see runListingCrops). Every one is one run after another — the store holds one
        // run's progress at a time.
        ...(submodels.length > 1 ? {
          options: [
            { key: 'current', label: t('notice.3d.matRunCurrent'), onRun: () => void cropAll([submodel]) },
            { key: 'all', label: t('notice.3d.matRunAll'), onRun: () => void cropAll(submodels.map((s) => s.id)) },
          ],
        } : {}),
        busy,
        disabled,
        onStop,
      }}
      promptLabel={t('notice.parts.listingPrompt')}
      load={load}
      revision={`${only ?? 'all'}:${photo ?? ''}`}
      promptNav={(
        <StepPromptNav
          steps={photos.map((_, n) => n + 1)}
          at={at}
          onGo={setPhotoAt}
          labelOf={(i) => t('notice.parts.listingShot').replace('{n}', String(i + 1))}
          vertical
        />
      )}
      answer={answer}
    />
  );
}
